import React, { useContext, useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  ButtonGroup,
  useDisclosure,
  Box,
  Flex,
  Text,
} from '@chakra-ui/react';
import { GridFour, Link as LinkIcon } from '@phosphor-icons/react';
import get from 'lodash/get';
import { useMutation } from '@apollo/client';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';

import SpinnerContext from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { s3 } from '../../../utils';
import { getEnvironment } from '../../../environment';
import { UPDATE_INTERNAL_CUSTOMER } from '../query';
import { QuillInput } from '../../QuillInput';

const { dOSpacesBucketName, dOCdnUrl } = getEnvironment();

const CreateNoteSchema = Yup.object().shape({
  message: Yup.string().required('Du musst einen Kommentar angeben'),
});

const CreateNewNote = ({ refetch, customerId, type }) => {
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateInternalCustomer] = useMutation(UPDATE_INTERNAL_CUSTOMER);
  const { isOpen: isOpenPopover, onOpen: openPopover, onClose: closePopover } = useDisclosure();
  const { toggleSpinner } = useContext(SpinnerContext);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();

  const handleCreateNewComment = async () => {};

  return (
    <>
      <Button
        colorScheme="green"
        variant="outline"
        size="sm"
        aria-label="Kommunikation protokollieren"
        onClick={openPopover}
        leftIcon={<GridFour />}
      >
        {type === 'COMMUNICATION' ? 'Kommunikation protokollieren' : 'Neuer Kommentar'}
      </Button>
      <Modal isOpen={isOpenPopover} onClose={closePopover} size="lg">
        <ModalOverlay />
        <ModalContent>
          <Formik
            validationSchema={CreateNoteSchema}
            initialValues={{ message: '', files: [], fileUploadBlobs: [] }}
            onSubmit={async ({ message, files, fileUploadBlobs }) => {
              toggleSpinner(true);
              try {
                let filesToConnect = [];
                if (fileUploadBlobs) {
                  let fileUploadPromises = Array.from(fileUploadBlobs).map((fileBlob, i) => {
                    const index = fileBlob.name.lastIndexOf('.');
                    const extension = fileBlob.name.substring(index + 1);
                    const Key = `notes/${customerId}-${i}-${moment().unix()}.${extension}`;

                    const photoUrl = `https://${dOSpacesBucketName}.${dOCdnUrl}/${Key}`;
                    filesToConnect.push(photoUrl);

                    return s3
                      .putObject({
                        Body: fileBlob,
                        Bucket: dOSpacesBucketName,
                        Key,
                        ACL: 'public-read',
                      })
                      .promise();
                  });
                  await Promise.all(fileUploadPromises);
                }
                await updateInternalCustomer({
                  variables: {
                    where: {
                      id: customerId,
                    },
                    data: {
                      updatedAtRandomizer: Math.random().toString(),
                      notes: {
                        create: [
                          {
                            message,
                            type,
                            connectedFiles:
                              filesToConnect.length > 0
                                ? {
                                    create: filesToConnect.map((uri, index) => {
                                      return {
                                        uri,
                                        name: fileUploadBlobs[index].name,
                                        type: fileUploadBlobs[index].type,
                                      };
                                    }),
                                  }
                                : undefined,
                            commenter: {
                              connect: { id: user.id },
                            },
                          },
                        ],
                      },
                    },
                  },
                });
                await refetch();
                closePopover();
                setMessage('');
              } catch (error) {
                console.log(error);

                toast({
                  description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                  status: 'error',
                });
              }
              toggleSpinner(false);
            }}
          >
            {({ setFieldValue, values }) => {
              return (
                <Form>
                  <ModalHeader>{type === 'COMMUNICATION' ? 'Kommunikation protokollieren' : 'Neuer Kommentar'}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <QuillInput
                      label={type === 'COMMUNICATION' ? 'Deine Kommunikation' : 'Dein Kommentar'}
                      name="message"
                      placeholder="Gib hier Deine Nachricht ein..."
                    />
                    <Box>
                      <Button
                        mt="5"
                        type="button"
                        size="sm"
                        onClick={() => {
                          fileInputRef && fileInputRef.current && fileInputRef.current?.click();
                        }}
                      >
                        Dateien hochladen
                      </Button>
                      <input
                        ref={fileInputRef}
                        hidden={true}
                        type="file"
                        name="files"
                        multiple
                        onChange={async (event) => {
                          let eventFiles = event.target.files;
                          let fileUploadBlobs = [...values.fileUploadBlobs, ...Array.from(eventFiles)];
                          let uploadedFiles = Array.from(event.target.files).map((file) => {
                            let reader = new FileReader();

                            return new Promise((resolve) => {
                              // Resolve the promise after reading file
                              reader.onload = () => resolve(reader.result);
                              // Read the file as a text
                              reader.readAsDataURL(file);
                            });
                          });
                          let res = await Promise.all(uploadedFiles);
                          setFieldValue('files', [...values.files, ...res]);
                          setFieldValue('fileUploadBlobs', fileUploadBlobs);
                        }}
                      />
                    </Box>
                    {values.fileUploadBlobs.length > 0 ? (
                      <Flex direction="column" my="4">
                        {values.fileUploadBlobs.map(({ name }, index) => {
                          return (
                            <Flex key={`file-${index}`} mb="2">
                              <LinkIcon />
                              <Text flex="1" fontSize="sm">
                                {name}
                              </Text>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => {
                                  const clone1 = [...values.files];
                                  const clone2 = [...values.fileUploadBlobs];

                                  clone1.splice(index, 1);
                                  clone2.splice(index, 1);

                                  setFieldValue('files', clone1);
                                  setFieldValue('fileUploadBlobs', clone2);
                                }}
                              >
                                Entfernen
                              </Button>
                            </Flex>
                          );
                        })}
                      </Flex>
                    ) : null}
                  </ModalBody>

                  <ModalFooter>
                    <ButtonGroup size="sm">
                      <Button type="submit" colorScheme="green">
                        Fertig
                      </Button>
                    </ButtonGroup>
                  </ModalFooter>
                </Form>
              );
            }}
          </Formik>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateNewNote;
